import { create } from "zustand";
import { makeObject, objectFootprint, OBJECT_DEFS } from "./objectDefs";

const DEFAULT_FLOOR = {
  shape: "rectangle", // rectangle | l_shape | polygon
  // Polygon vertices in mm, anti-clockwise. Origin at top-left of building bbox.
  vertices: [
    { x: 0, y: 0 },
    { x: 30000, y: 0 },
    { x: 30000, y: 20000 },
    { x: 0, y: 20000 },
  ],
  wall_height: 6000,
  obstacles: [], // {id, type, x, y, length, width, label}
  doors: [], // {id, side?, x, y, width, kind: 'entry'|'loading'|'fire_exit'}
};

export const useEditor = create((set, get) => ({
  projectId: null,
  projectName: "Untitled Project",
  clientName: "",
  projectType: "warehouse", // 'warehouse' | 'supermarket'
  units: "mm",
  ceilingHeight: 6000,

  floor: DEFAULT_FLOOR,
  objects: [],
  selectedId: null,

  // Undo / redo — history of {floor, objects} snapshots. Auto-populated by a
  // subscription (see bottom of file) whenever floor or objects change while
  // `_undoing` is false. Cap at 50 recent entries to bound memory.
  past: [],
  future: [],
  _undoing: false,
  view: "split", // 2d | 3d | split
  snap: 100, // mm grid snap step
  // Smart-snap = flush-align object edges to the nearest wall or neighbour edge
  // during drag. Independent of grid `snap`. Off by default; toggled from Toolbar.
  smartSnap: true,
  // Desired aisle gap for smart-snap. When > 0, dragging an object near a wall
  // or another object snaps at EITHER flush (0 gap) OR (aisleGap) distance —
  // whichever the pointer is closer to. Set to 0 for flush-only behaviour.
  aisleGap: 1000,
  // showAisles: display persistent aisle-width dimension lines between parallel
  // rack rows. Independent of the smart-snap aisleGap. Toggle from the Toolbar.
  showAisles: false,
  // Compliance check — flag aisles narrower than the MHE turning radius.
  compliance: {
    enabled: false,
    preset: "reach_truck", // key from MHE_PRESETS
    threshold: 2700,       // mm; kept in sync with preset unless preset==='custom'
  },
  showGrid: true,
  showDimensions: true,
  // Fit-to-view: canvases subscribe to `fitVersion`; incrementing it re-frames.
  fitVersion: 0,

  // Hand tool = pan-only mode. When on, left-drag pans the view and no
  // object / vertex / obstacle picking is possible.
  handMode: false,
  // Environment presets for the 3D scene
  skyPreset: "day",       // day | sunset | overcast | night
  groundStyle: "plain",   // plain | grass | asphalt
  // measure tool — measuring-tape style. Only one active measurement at a time.
  // measure = null | { a: {x,y}, b?: {x,y} }.  Missing b means we're still placing.
  measureMode: false,
  measure: null,
  cursorMm: null, // {x,y} live cursor position in floor mm

  // ----- bootstrap -----
  loadProject(p) {
    set({
      projectId: p.id,
      projectName: p.name || "Untitled Project",
      clientName: p.client_name || "",
      projectType: p.project_type || "warehouse",
      units: p.units || "mm",
      ceilingHeight: p.ceiling_height || 6000,
      floor: { ...DEFAULT_FLOOR, ...(p.floor_plan || {}) },
      objects: p.objects || [],
      selectedId: null,
      // Reset history so users can't undo past the initial state. The
      // `_undoing` flag prevents the auto-history subscription from recording
      // this load as a new step.
      past: [],
      future: [],
      _undoing: true,
    });
    queueMicrotask(() => set({ _undoing: false }));
  },

  exportPayload() {
    const s = get();
    return {
      name: s.projectName,
      client_name: s.clientName,
      project_type: s.projectType,
      units: s.units,
      ceiling_height: s.ceilingHeight,
      floor_plan: s.floor,
      objects: s.objects,
    };
  },

  // ----- floor -----
  setFloor(floor) {
    set({ floor: { ...get().floor, ...floor } });
  },
  setFloorShape(shape) {
    if (shape === "rectangle") {
      set({
        floor: {
          ...get().floor,
          shape,
          vertices: [
            { x: 0, y: 0 },
            { x: 30000, y: 0 },
            { x: 30000, y: 20000 },
            { x: 0, y: 20000 },
          ],
        },
      });
    } else if (shape === "l_shape") {
      set({
        floor: {
          ...get().floor,
          shape,
          vertices: [
            { x: 0, y: 0 },
            { x: 30000, y: 0 },
            { x: 30000, y: 12000 },
            { x: 18000, y: 12000 },
            { x: 18000, y: 22000 },
            { x: 0, y: 22000 },
          ],
        },
      });
    } else if (shape === "polygon") {
      set({ floor: { ...get().floor, shape, vertices: [] } });
    }
  },
  setVertex(i, v) {
    const verts = [...get().floor.vertices];
    verts[i] = v;
    set({ floor: { ...get().floor, vertices: verts } });
  },
  addVertex(v) {
    const verts = [...get().floor.vertices, v];
    set({ floor: { ...get().floor, vertices: verts } });
  },
  setWallLength(i, lengthMm) {
    // Adjust vertex i+1 along the segment (i -> i+1) so its length matches lengthMm.
    const verts = [...get().floor.vertices];
    const a = verts[i];
    const b = verts[(i + 1) % verts.length];
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const cur = Math.hypot(dx, dy);
    if (cur === 0) return;
    const f = lengthMm / cur;
    const nx = a.x + dx * f;
    const ny = a.y + dy * f;
    verts[(i + 1) % verts.length] = { x: nx, y: ny };
    set({ floor: { ...get().floor, vertices: verts } });
  },
  setWallHeight(h) {
    set({ floor: { ...get().floor, wall_height: h } });
  },

  // Resize the floor polygon to a given bounding box (in mm).
  // For a rectangle, this rebuilds the rectangle exactly. For any other
  // shape, it linearly scales the existing vertices around the origin
  // so the new bounding-box matches.
  setBoundingSize({ length, width }) {
    const floor = get().floor;
    const verts = floor.vertices || [];
    if (verts.length < 2) return;
    const nl = Math.max(500, Number(length) || 0);
    const nw = Math.max(500, Number(width) || 0);
    if (floor.shape === "rectangle") {
      set({
        floor: {
          ...floor,
          vertices: [
            { x: 0, y: 0 },
            { x: nl, y: 0 },
            { x: nl, y: nw },
            { x: 0, y: nw },
          ],
        },
      });
      return;
    }
    const xs = verts.map((v) => v.x);
    const ys = verts.map((v) => v.y);
    const minX = Math.min(...xs), maxX = Math.max(...xs);
    const minY = Math.min(...ys), maxY = Math.max(...ys);
    const curL = maxX - minX || 1;
    const curW = maxY - minY || 1;
    const sx = nl / curL;
    const sy = nw / curW;
    const newVerts = verts.map((v) => ({
      x: (v.x - minX) * sx,
      y: (v.y - minY) * sy,
    }));
    set({ floor: { ...floor, vertices: newVerts } });
  },

  toggleHand() {
    const next = !get().handMode;
    // Turning on the hand tool implicitly leaves measure mode.
    set({ handMode: next, measureMode: next ? false : get().measureMode, selectedId: next ? null : get().selectedId });
  },

  fitView() {
    set({ fitVersion: get().fitVersion + 1 });
  },
  setSkyPreset(v) { set({ skyPreset: v }); },
  setGroundStyle(v) { set({ groundStyle: v }); },
  toggleSmartSnap() { set({ smartSnap: !get().smartSnap }); },
  setAisleGap(v) { set({ aisleGap: Math.max(0, Number(v) || 0) }); },
  toggleShowAisles() { set({ showAisles: !get().showAisles }); },
  setCompliance(patch) {
    set({ compliance: { ...get().compliance, ...patch } });
  },
  toggleCompliance() {
    const c = get().compliance;
    set({ compliance: { ...c, enabled: !c.enabled } });
  },

  // ----- measure (tape) -----
  toggleMeasure() {
    const next = !get().measureMode;
    set({ measureMode: next, measure: null, handMode: next ? false : get().handMode, selectedId: next ? null : get().selectedId });
  },
  addMeasurePoint(pt) {
    const s = get();
    // No active measurement → start one
    if (!s.measure) { set({ measure: { a: pt } }); return; }
    // First point placed, need second → complete
    if (!s.measure.b) { set({ measure: { a: s.measure.a, b: pt } }); return; }
    // Already completed → start a fresh one on next click (replaces previous)
    set({ measure: { a: pt } });
  },
  clearMeasure() {
    set({ measure: null });
  },
  setCursorMm(pt) {
    set({ cursorMm: pt });
  },

  // ----- obstacles / doors -----
  addObstacle(kind, position) {
    // Use OBJECT_DEFS defaults so obstacle dimensions match the library
    // schema (pillar 500×500×6000, office 6000×4000×3000, dock 3000×1500×200).
    const d = (OBJECT_DEFS[kind] && OBJECT_DEFS[kind].defaults) || {};
    const ob = {
      id: `ob_${Math.random().toString(36).slice(2, 10)}`,
      kind,
      x: position?.x ?? 1000,
      y: position?.y ?? 1000,
      length: d.length || (kind === "pillar" ? 500 : 4000),
      width:  d.width  || (kind === "pillar" ? 500 : 3000),
      height: d.height || (kind === "pillar" ? 6000 : 3000),
      label: (OBJECT_DEFS[kind] && OBJECT_DEFS[kind].label) || kind,
    };
    set({ floor: { ...get().floor, obstacles: [...get().floor.obstacles, ob] } });
  },
  updateObstacle(id, patch) {
    set({
      floor: {
        ...get().floor,
        obstacles: get().floor.obstacles.map((o) => (o.id === id ? { ...o, ...patch } : o)),
      },
    });
  },
  removeObstacle(id) {
    set({
      floor: {
        ...get().floor,
        obstacles: get().floor.obstacles.filter((o) => o.id !== id),
      },
    });
  },

  // ----- doors (entry / exit / fire exit / loading dock) -----
  // Doors live ON a wall edge, indexed by `edge_idx`. `offset` is the distance
  // in mm from the edge's start vertex to the door's start along the edge.
  addDoor(kind = "entry", edge_idx = 0) {
    const dfl = {
      entry:     { width: 1200, height: 2100 },
      exit:      { width: 1200, height: 2100 },
      fire_exit: { width: 900,  height: 2100 },
      loading:   { width: 3000, height: 3500 },
    }[kind] || { width: 1200, height: 2100 };
    const door = {
      id: `door_${Math.random().toString(36).slice(2, 10)}`,
      kind,
      edge_idx,
      offset: 2000,
      width: dfl.width,
      height: dfl.height,
    };
    const floor = get().floor;
    set({ floor: { ...floor, doors: [...(floor.doors || []), door] } });
  },
  updateDoor(id, patch) {
    const floor = get().floor;
    set({
      floor: {
        ...floor,
        doors: (floor.doors || []).map((d) => (d.id === id ? { ...d, ...patch } : d)),
      },
    });
  },
  removeDoor(id) {
    const floor = get().floor;
    set({
      floor: {
        ...floor,
        doors: (floor.doors || []).filter((d) => d.id !== id),
      },
    });
  },

  // ----- objects -----
  addObject(type, pos) {
    const existing = get().objects.length;
    const offset = existing * 1500;
    const finalPos = pos || { x: 2000 + (offset % 12000), y: 2000 + Math.floor(offset / 12000) * 3000 };
    const obj = makeObject(type, finalPos);
    set({ objects: [...get().objects, obj], selectedId: obj.id });
  },
  updateObject(id, patch) {
    set({
      objects: get().objects.map((o) => (o.id === id ? { ...o, ...patch } : o)),
    });
  },
  updateObjectProps(id, propPatch) {
    set({
      objects: get().objects.map((o) =>
        o.id === id ? { ...o, props: { ...o.props, ...propPatch } } : o
      ),
    });
  },
  removeObject(id) {
    set({
      objects: get().objects.filter((o) => o.id !== id),
      selectedId: get().selectedId === id ? null : get().selectedId,
    });
  },
  duplicateObject(id) {
    const o = get().objects.find((x) => x.id === id);
    if (!o) return;
    const clone = {
      ...o,
      id: `obj_${Math.random().toString(36).slice(2, 10)}`,
      position: { x: o.position.x + 1000, y: o.position.y + 1000 },
    };
    set({ objects: [...get().objects, clone], selectedId: clone.id });
  },

  /**
   * Clone the object N-1 additional times along an axis, spaced by its own
   * length (+ optional gap). Direction defaults to X-forward but respects the
   * object's Y-rotation so a rotated rack duplicates along its own long axis.
   *   axis: 'length' — clone along the rack's long side (X of local frame)
   *         'width'  — clone along the rack's short side (Y of local frame)
   */
  duplicateRow(id, count = 5, axis = "length", gap = 0) {
    const src = get().objects.find((x) => x.id === id);
    if (!src || count < 2) return;
    const fp = objectFootprint(src);
    const step = (axis === "width" ? fp.width : fp.length) + Number(gap || 0);
    // Rotation-aware direction: the object's rotY rotates its local X-axis in
    // the top-down plane; convert to world dx / dy per clone.
    const rotY = typeof src.rotation === "number" ? src.rotation : (src.rotation?.y || 0);
    const rad = (rotY * Math.PI) / 180;
    // For axis === 'length' the local direction is (1,0); for 'width' it's (0,1).
    const local = axis === "width" ? [0, 1] : [1, 0];
    const dx = (local[0] * Math.cos(rad) - local[1] * Math.sin(rad)) * step;
    const dy = (local[0] * Math.sin(rad) + local[1] * Math.cos(rad)) * step;
    const clones = [];
    for (let i = 1; i < count; i++) {
      clones.push({
        ...src,
        id: `obj_${Math.random().toString(36).slice(2, 10)}`,
        position: {
          x: Math.round(src.position.x + dx * i),
          y: Math.round(src.position.y + dy * i),
        },
      });
    }
    set({ objects: [...get().objects, ...clones] });
  },
  rotateObject(id, deltaDeg = 90, axis = "y") {
    set({
      objects: get().objects.map((o) => {
        if (o.id !== id) return o;
        const r = typeof o.rotation === "number"
          ? { x: 0, y: o.rotation, z: 0 }
          : { x: 0, y: 0, z: 0, ...o.rotation };
        r[axis] = (r[axis] + deltaDeg) % 360;
        return { ...o, rotation: r };
      }),
    });
  },
  setSelected(id) {
    set({ selectedId: id });
  },
  setView(v) {
    set({ view: v });
  },
  setProjectName(name) {
    set({ projectName: name });
  },
  setClientName(name) {
    set({ clientName: name });
  },
  setProjectType(t) {
    set({ projectType: t });
  },

  // ----- undo / redo -----
  undo() {
    const s = get();
    if (!s.past.length) return;
    const prev = s.past[s.past.length - 1];
    set({
      _undoing: true,
      past: s.past.slice(0, -1),
      future: [...s.future, { floor: s.floor, objects: s.objects }],
      floor: prev.floor,
      objects: prev.objects,
      selectedId: null,
    });
    // Release the flag next tick so subscribers see the change but don't
    // re-record it as a new history entry.
    queueMicrotask(() => set({ _undoing: false }));
  },
  redo() {
    const s = get();
    if (!s.future.length) return;
    const next = s.future[s.future.length - 1];
    set({
      _undoing: true,
      future: s.future.slice(0, -1),
      past: [...s.past, { floor: s.floor, objects: s.objects }],
      floor: next.floor,
      objects: next.objects,
      selectedId: null,
    });
    queueMicrotask(() => set({ _undoing: false }));
  },
}));

// Auto-history: whenever `floor` or `objects` change (and we're NOT in the
// middle of an undo/redo), push the PREVIOUS snapshot onto the past stack and
// clear the future (branching invalidates redo history).
useEditor.subscribe((state, prev) => {
  if (state._undoing) return;
  if (state.floor === prev.floor && state.objects === prev.objects) return;
  const past = [...prev.past, { floor: prev.floor, objects: prev.objects }].slice(-50);
  // Use setState directly to avoid triggering another subscription round.
  useEditor.setState({ past, future: [] });
});
